import React, { useState, useEffect } from 'react';
import { PlayIcon, StopIcon, ClockIcon, PencilIcon, TrashIcon, PlusIcon, PauseIcon } from '@heroicons/react/24/outline';
import { TimeEntry } from '../../types/task';
import {
  startTimer,
  stopTimer,
  pauseTimer,
  resumeTimer,
  getTaskTimeEntries,
  getRunningTimer,
  deleteTimeEntry,
  formatDuration,
  formatDurationHumanReadable,
  getElapsedTime,
  getTotalTaskTime,
  addManualTimeEntry,
  updateTimeEntry
} from '../../services/timeTrackingService';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import TimeEntryForm, { TimeEntryFormData } from './TimeEntryForm';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';

interface TimeTrackerProps {
  taskId: string;
  organizationId: string;
}

export default function TimeTracker({ taskId, organizationId }: TimeTrackerProps) {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [runningTimer, setRunningTimer] = useState<TimeEntry | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [loading, setLoading] = useState(false);
  const [description, setDescription] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntry | undefined>();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<string | null>(null);
  const [updatingEntryId, setUpdatingEntryId] = useState<string | null>(null);

  // Időbejegyzések betöltése
  const loadTimeEntries = async () => {
    if (!currentUser) return;
    
    try {
      const entries = await getTaskTimeEntries(taskId, organizationId);
      setTimeEntries(entries);
      
      const total = await getTotalTaskTime(taskId, organizationId);
      setTotalTime(total);
      
      // Futó időmérő ellenőrzése
      const running = await getRunningTimer(taskId, currentUser.uid, organizationId);
      setRunningTimer(running);
      
      if (running && !running.isPaused) {
        // Csak akkor frissítsük az időt, ha nem szünetel
        const totalElapsed = getElapsedTime(running.startTime);
        const pausedDuration = running.pausedDuration || 0;
        setElapsedTime(totalElapsed - pausedDuration);
      }
    } catch (error) {
      console.error('Failed to load time entries:', error);
    }
  };

  useEffect(() => {
    loadTimeEntries();
    
    // Időbejegyzések real-time figyelése (5 másodpercenként)
    const interval = setInterval(() => {
      loadTimeEntries();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [taskId, organizationId, currentUser]);

  // Futó időmérő frissítése másodpercenként
  useEffect(() => {
    if (!runningTimer) return;

    const interval = setInterval(() => {
      if (runningTimer.isPaused) {
        // Ha szünetel, ne frissítsük az időt
        return;
      }
      
      const totalElapsed = getElapsedTime(runningTimer.startTime);
      const pausedDuration = runningTimer.pausedDuration || 0;
      setElapsedTime(totalElapsed - pausedDuration);
    }, 1000);

    return () => clearInterval(interval);
  }, [runningTimer]);

  const handleStart = async () => {
    if (!currentUser) return;
    
    if (!description.trim()) {
      alert(t('timeTracking.descriptionRequired'));
      return;
    }
    
    setLoading(true);
    try {
      const timer = await startTimer(
        taskId,
        currentUser.uid,
        currentUser.displayName || currentUser.email || 'Unknown',
        organizationId,
        description
      );
      setRunningTimer(timer);
      setDescription('');
      await loadTimeEntries();
    } catch (error: any) {
      alert(error.message || 'Failed to start timer');
    } finally {
      setLoading(false);
    }
  };

  const handlePause = async () => {
    if (!runningTimer) return;
    
    setLoading(true);
    try {
      const pausedTimer = await pauseTimer(runningTimer.id);
      setRunningTimer(pausedTimer);
      // Azonnal frissítjük az időt
      const totalElapsed = getElapsedTime(pausedTimer.startTime);
      const pausedDuration = pausedTimer.pausedDuration || 0;
      setElapsedTime(totalElapsed - pausedDuration);
    } catch (error) {
      alert('Failed to pause timer');
    } finally {
      setLoading(false);
    }
  };

  const handleResume = async () => {
    if (!runningTimer) return;
    
    setLoading(true);
    try {
      const resumedTimer = await resumeTimer(runningTimer.id);
      setRunningTimer(resumedTimer);
    } catch (error) {
      alert('Failed to resume timer');
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async () => {
    if (!runningTimer) return;
    
    setLoading(true);
    try {
      await stopTimer(runningTimer.id);
      setRunningTimer(null);
      setElapsedTime(0);
      await loadTimeEntries();
    } catch (error) {
      alert('Failed to stop timer');
    } finally {
      setLoading(false);
    }
  };

  const toggleBillable = async (entryId: string, newValue: boolean) => {
    setUpdatingEntryId(entryId);
    try {
      await updateTimeEntry(entryId, { isBillable: newValue });
      await loadTimeEntries();
    } catch (error) {
      console.error('Error updating billable status:', error);
      alert('Failed to update billable status');
    } finally {
      setUpdatingEntryId(null);
    }
  };

  const handleDeleteClick = (entryId: string) => {
    setEntryToDelete(entryId);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!entryToDelete) return;
    
    try {
      await deleteTimeEntry(entryToDelete);
      await loadTimeEntries();
      setDeleteConfirmOpen(false);
      setEntryToDelete(null);
    } catch (error) {
      alert('Failed to delete time entry');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setEntryToDelete(null);
  };

  const handleManualEntry = async (data: TimeEntryFormData) => {
    if (!currentUser) return;

    if (editingEntry) {
      // Szerkesztés
      await updateTimeEntry(editingEntry.id, {
        description: data.description,
        startTime: data.startTime,
        endTime: data.endTime,
        isBillable: data.isBillable
      });
    } else {
      // Új létrehozása
      await addManualTimeEntry(
        taskId,
        currentUser.uid,
        currentUser.displayName || currentUser.email || 'Unknown',
        organizationId,
        data.startTime,
        data.endTime,
        data.description,
        data.isBillable
      );
    }

    await loadTimeEntries();
    setIsFormOpen(false);
    setEditingEntry(undefined);
  };

  const handleEdit = (entry: TimeEntry) => {
    setEditingEntry(entry);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingEntry(undefined);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('hu-HU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <div className="space-y-2 md:space-y-4">
      {/* Időmérő vezérlők */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-2 md:p-4">
        <div className="flex items-center justify-between mb-2 md:mb-4">
          <div className="flex items-center gap-1.5 md:gap-2">
            <ClockIcon className="h-4 w-4 md:h-5 md:w-5 text-gray-400" />
            <h3 className="text-sm md:text-lg font-medium text-gray-900 dark:text-white">
              {t('timeTracking.title')}
            </h3>
          </div>
          <div className="text-right">
            <div className="text-lg md:text-2xl font-mono font-bold text-gray-900 dark:text-white">
              {runningTimer ? formatDuration(elapsedTime) : '00:00:00'}
            </div>
            <div className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
              {t('timeTracking.totalTime')}: {formatDurationHumanReadable(Math.floor(totalTime * 3600))}
            </div>
          </div>
        </div>

        {!runningTimer ? (
          <div className="space-y-3">
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('timeTracking.placeholder')}
              className="w-full px-2 md:px-3 py-1.5 md:py-2 border border-gray-300 dark:border-gray-600 rounded-md md:rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
            <button
              onClick={handleStart}
              disabled={loading}
              className="w-full flex items-center justify-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-green-600 hover:bg-green-700 text-white rounded-md md:rounded-lg transition-colors disabled:opacity-50 text-sm md:text-base"
            >
              <PlayIcon className="h-4 w-4 md:h-5 md:w-5" />
              <span>{t('timeTracking.startTimer')}</span>
            </button>
          </div>
        ) : (
          <div className="space-y-2 md:space-y-3">
            {runningTimer.description && (
              <div className="text-xs md:text-sm text-gray-600 dark:text-gray-400">
                {runningTimer.description}
              </div>
            )}
            {runningTimer.isPaused && (
              <div className="text-xs md:text-sm text-yellow-600 dark:text-yellow-400 font-medium">
                ⏸️ {t('timeTracking.paused') || 'Szüneteltetve'}
              </div>
            )}
            <div className="flex gap-1.5 md:gap-2">
              {runningTimer.isPaused ? (
                <button
                  onClick={handleResume}
                  disabled={loading}
                  className="flex-1 flex items-center justify-center gap-1 md:gap-2 px-2 md:px-4 py-1.5 md:py-2 bg-green-600 hover:bg-green-700 text-white rounded-md md:rounded-lg transition-colors disabled:opacity-50 text-xs md:text-base"
                >
                  <PlayIcon className="h-4 w-4 md:h-5 md:w-5" />
                  <span className="hidden sm:inline">{t('timeTracking.resume') || 'Folytatás'}</span>
                  <span className="sm:hidden">Folyt.</span>
                </button>
              ) : (
                <button
                  onClick={handlePause}
                  disabled={loading}
                  className="flex-1 flex items-center justify-center gap-1 md:gap-2 px-2 md:px-4 py-1.5 md:py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-md md:rounded-lg transition-colors disabled:opacity-50 text-xs md:text-base"
                >
                  <PauseIcon className="h-4 w-4 md:h-5 md:w-5" />
                  <span className="hidden sm:inline">{t('timeTracking.pause') || 'Szünet'}</span>
                  <span className="sm:hidden">Szünet</span>
                </button>
              )}
              <button
                onClick={handleStop}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-1 md:gap-2 px-2 md:px-4 py-1.5 md:py-2 bg-red-600 hover:bg-red-700 text-white rounded-md md:rounded-lg transition-colors disabled:opacity-50 text-xs md:text-base"
              >
                <StopIcon className="h-4 w-4 md:h-5 md:w-5" />
                <span className="hidden sm:inline">{t('timeTracking.stopTimer')}</span>
                <span className="sm:hidden">Stop</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Időbejegyzések lista */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-2 md:px-4 py-2 md:py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2">
          <h4 className="text-xs md:text-sm font-medium text-gray-900 dark:text-white">
            {t('timeTracking.timeEntries')} ({timeEntries.length})
          </h4>
          <button
            onClick={() => setIsFormOpen(true)}
            className="flex items-center gap-1 px-2 md:px-3 py-1 md:py-1.5 text-xs md:text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md md:rounded-lg transition-colors"
          >
            <PlusIcon className="h-3.5 w-3.5 md:h-4 md:w-4" />
            <span className="hidden sm:inline">{t('timeTracking.addManual')}</span>
            <span className="sm:hidden">+</span>
          </button>
        </div>
        
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {timeEntries.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
              {t('timeTracking.noEntries')}
            </div>
          ) : (
            timeEntries.map((entry) => (
              <div key={entry.id} className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <span className="text-lg font-mono font-semibold text-gray-900 dark:text-white">
                        {entry.duration ? formatDuration(entry.duration) : '00:00:00'}
                      </span>
                      <button
                        onClick={() => toggleBillable(entry.id, !entry.isBillable)}
                        disabled={updatingEntryId === entry.id || !entry.endTime}
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
                          entry.isBillable
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 hover:bg-green-200'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 hover:bg-yellow-200'
                        }`}
                        title={entry.isBillable ? 'Számlázható - kattints az ellenőrzés alatt állításhoz' : 'Ellenőrzés alatt - kattints a számlázhatóra állításhoz'}
                      >
                        {updatingEntryId === entry.id ? (
                          <span className="animate-pulse">...</span>
                        ) : (
                          entry.isBillable ? t('timeTracking.billable') + ' ✓' : 'Ellenőrzés alatt'
                        )}
                      </button>
                      {!entry.endTime && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded animate-pulse">
                          {t('timeTracking.running')}
                        </span>
                      )}
                    </div>
                    
                    {entry.description && (
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        {entry.description}
                      </p>
                    )}
                    
                    <div className="mt-1 flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                      <span>{entry.userName}</span>
                      <span>{formatDate(entry.startTime)}</span>
                      {entry.endTime && (
                        <span>→ {formatDate(entry.endTime)}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleEdit(entry)}
                      className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                      title={t('timeTracking.edit')}
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(entry.id)}
                      className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                      title={t('timeTracking.delete')}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Manuális időbejegyzés form */}
      {currentUser && (
        <TimeEntryForm
          isOpen={isFormOpen}
          onClose={handleCloseForm}
          onSubmit={handleManualEntry}
          initialData={editingEntry}
          userName={currentUser.displayName || currentUser.email || 'Unknown'}
        />
      )}

      {/* Törlés megerősítés */}
      <ConfirmDeleteModal
        isOpen={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('timeTracking.deleteTitle')}
        deleteMessage={t('timeTracking.deleteMessage')}
        deleteWarning={t('timeTracking.deleteWarning')}
        zIndex={100}
      />
    </div>
  );
}
